import { useContext, useState, useEffect } from "react"
import { useQuery, useLazyQuery, useApolloClient } from "@apollo/client"
import { Link } from "react-router-dom"
import { DataTable } from "primereact/datatable"
import { Column } from "primereact/column"
import { Button } from "primereact/button"
import { Dropdown } from "primereact/dropdown"
import { excludedFields } from "./utils"
import { DateTime } from "luxon"
import ParticipantSidebar from "../../components/sidebars/ParticipantSidebar"
import PageSidebarContent from "../../components/sidebars/PageSidebarContent"
import useLabels from "../../hooks/useLabels"
import { UserContext } from "../../context/userContext"
import { LoaderMedium } from "../../components/Loaders"
import _ from "lodash"
import { cleanupTmpFields } from "../../components/FieldTypes/helpers/cleanupTempFields"
import GLOBALS_STATUS from "../../config/globalsStatus"
import { GET_SAVED_BLOCKS, GET_CUSTOM_FIELD, GET_STAFF_NAME } from "./queries"
import { classNames } from "primereact/utils"
import styles from "../../styles/Page-RecoveryPlan.module.css"

const NoRecoveryPlan = ({ label, shouldShowButton }) => (
  <>
    <PageSidebarContent>
      <ParticipantSidebar />
    </PageSidebarContent>
    <div className="main-content">
      <h1 className={styles.title}>{label}</h1>
      {shouldShowButton && (
        <Link to="/form/recovery_plan_engagement/1" className="btn">
          Begin {label}
        </Link>
      )}
      <p>No {label} has been created.</p>
    </div>
  </>
)

// Utility function to determine field type
const determineFieldType = (fieldName) =>
  fieldName.startsWith("custom_") ? "custom" : "system"

// Safe fetch for custom fields
const safeFetchCustomField = async (client, idField, type) => {
  if (!idField || !type) {
    console.warn(`Skipping fetch: idField=${idField}, type=${type}`)
    return { idField, label: "Missing required variables" }
  }
  try {
    const res = await client.query({
      query: GET_CUSTOM_FIELD,
      variables: { idField, type },
      fetchPolicy: "network-only",
    })
    return {
      idField,
      label: res.data?.fieldByName?.field_label || "Unknown label",
    }
  } catch (error) {
    console.error("Error fetching custom field:", error)
    return { idField, label: "Error fetching label" }
  }
}

// Safe fetch for staff names
const safeFetchStaffName = async (fetchFunction, staff_id, organization_id) => {
  if (!staff_id || !organization_id) {
    console.warn(
      `Skipping fetchStaffName: staff_id=${staff_id}, organization_id=${organization_id}`
    )
    return { data: { getStaffName: { data: "Unknown" } } }
  }

  return await fetchFunction({ variables: { staff_id, organization_id } })
}

export default function RecoveryPlan() {
  const [blocks, setBlocks] = useState(null)
  const [loadingLabels, labels] = useLabels(["recovery_plan_engagement"])
  const [isProcessing, setIsProcessing] = useState(true)
  const client = useApolloClient()
  const [selectedGoal, setSelectedGoal] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedStatus, setSelectedStatus] = useState(null)
  const userCtx = useContext(UserContext)
  const participant_id = userCtx.participant_id
  const organization_id = userCtx.session.user.organization_id
  const statusOptions = [
    { label: "All", value: null },
    { label: "Active", value: "Active" },
    { label: "Inactive", value: "Inactive" },
    { label: "In the future", value: "In the future" },
    { label: "Complete", value: "Complete" },
  ]

  // Lazy queries
  const [getStaffName] = useLazyQuery(GET_STAFF_NAME, {
    fetchPolicy: "network-only",
    onError: (error) => console.error("Error fetching staff name:", error),
  })

  useEffect(() => {
    const handleKeydown = (event) => {
      if (event.key === "Escape") {
        setIsModalOpen(false)
      }
    }
    window.addEventListener("keydown", handleKeydown)
    return () => window.removeEventListener("keydown", handleKeydown)
  }, [])

  const { loading, error, data } = useQuery(GET_SAVED_BLOCKS, {
    variables: { participant_id, organization_id },
    fetchPolicy: "network-only",
    onError: () => {
      console.error("Error loading recovery plan data")
      setIsProcessing(false) // Detén el procesamiento en caso de error
    },
    onCompleted: async (data) => {
      setIsProcessing(true)
      const blocksData = data?.blockSaved?.data
      if (!blocksData || blocksData === "null") {
        setIsProcessing(false) // Termina el procesamiento si no hay datos

        setBlocks(null)
        return
      }

      try {
        const parsedBlocks = JSON.parse(blocksData)
        const cleanedValues = cleanupTmpFields(parsedBlocks?.values || {})

        const [createdBy, updatedBy] = await Promise.all([
          safeFetchStaffName(
            getStaffName,
            parsedBlocks?.staff_id,
            parsedBlocks?.organization_id
          ),
          safeFetchStaffName(
            getStaffName,
            parsedBlocks?.edited_by_staff_id,
            parsedBlocks?.organization_id
          ),
        ])

        const customFields = {
          supervision_plan_review_frequency:
            cleanedValues?.plan_review_frequency ||
            "No frequency provided",
          ..._.pickBy(
            cleanedValues,
            (value, key) =>
              key.startsWith("custom_") || !excludedFields.includes(key)
          ),
        }

        const results = await Promise.all(
          _.keys(customFields).map(async (idField) => {
            const type = determineFieldType(idField)
            return safeFetchCustomField(client, idField, type)
          })
        )

        const mixedArray = results.map((item) => ({
          label: item.label,
          value: customFields[item.idField],
        }))

        const formattedData = {
          created_by: createdBy.data.getStaffName.data || "",
          date_created: parsedBlocks?.date_created
            ? DateTime.fromSeconds(
              parsedBlocks.date_created._seconds
            ).toISODate()
            : null,
          date_modified: parsedBlocks?.date_modified
            ? DateTime.fromSeconds(
              parsedBlocks.date_modified._seconds
            ).toISODate()
            : null,
          updated_by: updatedBy.data.getStaffName.data || "",
          plan_recovery_progress: cleanedValues?.plan_recovery_progress,
          strengths_interests: cleanedValues?.strengths_interests || "",
          hope_scale: cleanedValues?.hope_scale || "",
          hopes_dreams: cleanedValues?.hopes_dreams || "",
          change_thing:
            cleanedValues?.change_thing || "No change provided",
          plan_complementary:
            cleanedValues?.plan_complementary || "No complementary plan",
          plan_complementary_notes:
            cleanedValues?.plan_complementary_notes,
          supportive_people: cleanedValues?.supportive_people || [],
          plan_goals: cleanedValues?.plan_goals || [],
          plan_review_frequency:
            cleanedValues?.plan_review_frequency || "",
          plan_review_other: cleanedValues?.plan_review_other || "",
          mixedArray,
        }

        setBlocks(formattedData)
      } catch (error) {
        console.error("Error parsing blocks data", error)
        setBlocks(null)
      } finally {
        setIsProcessing(false)
      }
    },
  })

  const renderHtmlContent = (content) => (
    <div dangerouslySetInnerHTML={{ __html: content || "" }} />
  )

  const shouldShowAddEngagementBtn = _.includes(
    GLOBALS_STATUS.ACTIVE_STATUS,
    userCtx.participant_membership_status
  )

  if (loading || isProcessing) return <LoaderMedium />
  if (error) return <p>Error loading recovery plan</p>
  if (!blocks)
    return (
      <NoRecoveryPlan
        label={labels.recovery_plan_engagement}
        shouldShowButton={shouldShowAddEngagementBtn}
      />
    )

  return (
    <>
      <PageSidebarContent>
        <ParticipantSidebar />
      </PageSidebarContent>
      <div className="main-content">
        <h1 className={styles.title}>{labels.recovery_plan_engagement}</h1>

        {shouldShowAddEngagementBtn && (
          <Link to="/form/recovery_plan_engagement/1" className="btn">
            Begin {labels.recovery_plan_engagement}
          </Link>
        )}

        <div className={classNames("box-summary", "no-title", styles.summaryBox)}>
          <ul>
            {/*"(only show Progress since last engagement if field is in the block) {plan_recovery_progress}*/}
            {blocks.plan_recovery_progress && (
              <li>
                <strong>Progress since last engagement:</strong>{" "}
                {blocks.plan_recovery_progress}
              </li>
            )}

            <li>
              <strong>Date Updated:</strong> {blocks.date_modified}
            </li>
            <li>
              <strong>Updated By:</strong> {blocks.updated_by}
            </li>
            <li>
              <strong>Date Created: </strong>
              {blocks.date_created}
            </li>
            <li>
              <strong>Created By: </strong>
              {blocks.created_by}
            </li>
          </ul>
        </div>

        <div className={styles.summaryContent}>
          {blocks.strengths_interests && (
            <>
              <div className={styles.colLeft}>
                <h4>Strengths and Interests</h4>
              </div>
              <div className={styles.colRight}>
                {renderHtmlContent(blocks.strengths_interests)}
              </div>
            </>
          )}

          {(blocks.hope_scale || blocks.hopes_dreams) && (
            <>
              <div className={styles.colLeft}>
                <h4>Hopes and dreams</h4>
              </div>
              <div className={styles.colRight}>
                {blocks.hope_scale}
                {blocks.hopes_dreams
                  ? renderHtmlContent(blocks.hopes_dreams)
                  : ""}
              </div>
            </>
          )}

          {blocks.change_thing && (
            <>
              <div className={styles.colLeft}>
                <h4>Change one thing?</h4>
              </div>
              <div className={styles.colRight}>
                {renderHtmlContent(blocks.change_thing)}
              </div>
            </>
          )}

          {blocks.plan_complementary && (
            <>
              <div className={styles.colLeft}>
                <h4>Complementary Plan</h4>
              </div>
              <div className={styles.colRight}>
                {blocks.plan_complementary === "Yes"
                  ? blocks.plan_complementary_notes
                  : "No complementary plan"}
              </div>
            </>
          )}
        </div>

        <h2 className={styles.subHeader}>Supportive People</h2>
        <DataTable value={blocks.supportive_people} responsiveLayout="scroll">
          <Column field="name" header="Name" />
          <Column field="relationship" header="Relationship" />
          <Column field="contact" header="Okay to contact?" />
          <Column field="support_phone" header="Phone" />
          <Column field="support_email" header="Email" />
        </DataTable>

        <h2 className={styles.subHeader}>Recovery Plan Goals</h2>
        <div style={{ marginBottom: "1em" }}>
          <Dropdown
            value={selectedStatus}
            options={statusOptions}
            onChange={(e) => setSelectedStatus(e.value)}
            placeholder="Filter by Status"
            className="p-column-filter"
          />
        </div>
        <DataTable
          value={blocks.plan_goals.filter(
            (goal) => !selectedStatus || goal.status === selectedStatus
          )}
          responsiveLayout="scroll"
        >
          <Column
            field="goal"
            header="SMART Goal"
            body={(rowData) => renderHtmlContent(rowData.goal)}
          />
          <Column field="domain" header="Recovery Domain" />
          <Column field="status" header="Status" />
          <Column field="priority_level" header="Priority" />
          <Column
            header="Action"
            body={(rowData) =>
              rowData.goal ? (
                <Button
                  onClick={() => {
                    setSelectedGoal(rowData)
                    setIsModalOpen(true)
                  }}
                >
                  View details
                </Button>
              ) : null
            }
          />
        </DataTable>

        <h2 className={styles.subHeader}>Plan Details</h2>
        <DataTable value={blocks.mixedArray} responsiveLayout="scroll">
          <Column field="label" />
          <Column field="value" />
        </DataTable>
      </div>

      {isModalOpen && selectedGoal && (
        <div
          className={styles.modalOverlay}
          onClick={() => setIsModalOpen(false)} // Cerrar el modal al hacer clic en el overlay
        >
          {/* Botón Close fuera de modalContent */}
          <button
            className={`${styles.closeButton} ${styles.closeButtonOutside}`}
            onClick={(e) => {
              e.stopPropagation() // Evita que el clic cierre el modal
              setIsModalOpen(false)
            }}
          >
            Close
          </button>

          <div
            className={styles.modalContent}
            onClick={(e) => e.stopPropagation()} // Evita cerrar el modal al hacer clic dentro
          >
            <h3>Goal Details</h3>

            {/* Goal Notes */}
            <div className={styles["notes-wrapper"]}>
              <p>
                <strong>Goal Notes</strong>
              </p>
              <ul>
                {selectedGoal.goal_notes.split("\n").map((note, index) => (
                  <li key={index}>{renderHtmlContent(note)}</li>
                ))}
              </ul>

              {/* Actions */}
              <p>
                <strong>Actions</strong>
              </p>
              <ul>
                {selectedGoal.recovery_plan_goals_actions.map(
                  (action, index) => (
                    <li key={index}>{action.action || "No action provided"}</li>
                  )
                )}
              </ul>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
